import React from "react";

import { formatPrice } from "@/src/utils/formatPrice";

import { figureColumn } from "./figureColumn";
import DataTableColumnHeader from "../../../dataTable/DataTableColumnHeader";
import { statusColumn } from "../../../dataTable/dataTableColumns";
import DataTableLinkCell from "../../../dataTable/DataTableLinkCell";
import { getOfferEditHref } from "../../../offers/tableParams/offerColumns";

import type { TFunction } from "i18next";

import type { OfferPurchaseRow } from "./offerPurchaseColumns.types";
import type { DataTableColumnDef } from "../../../dataTable/dataTable.types";

interface OfferPurchaseColumnsOptions {
  t: TFunction;
  projectSlug: string;
  canLink: boolean;
}

export function getOfferPurchaseHref(
  row: OfferPurchaseRow,
  projectSlug: string,
  canLink: boolean,
): string | undefined {
  return canLink ? getOfferEditHref(projectSlug, row.offer_uuid) : undefined;
}

const PURCHASE_TYPE_LIST = ["tvod", "svod", "tvod_bundle"] as const;

export function offerPurchaseColumns({
  t,
  projectSlug,
  canLink,
}: OfferPurchaseColumnsOptions): DataTableColumnDef<OfferPurchaseRow>[] {
  return [
    statusColumn<OfferPurchaseRow>("status", t("offer_status"), [
      { value: "active", label: t("table:status.active"), tone: "success" },
      { value: "stop", label: t("table:status.stop"), tone: "error" },
    ]),
    {
      accessorKey: "offer_label",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("offer_label")} />
      ),
      meta: { searchable: true, label: t("offer_label") },
      cell: ({ row }) => (
        <DataTableLinkCell
          href={getOfferPurchaseHref(row.original, projectSlug, canLink)}
        >
          {row.original.offer_label}
        </DataTableLinkCell>
      ),
    },
    {
      accessorKey: "description",
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("price_description")} />
      ),
      meta: {
        searchable: true,
        label: t("price_description"),
        width: "22%",
        minWidth: 220,
      },
      cell: ({ row }) => row.original.description,
    },
    {
      accessorKey: "type",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t("purchase_type_title")}
        />
      ),
      enableSorting: false,
      meta: {
        label: t("purchase_type_title"),
        width: "10%",
        minWidth: 105,
        filterOptions: PURCHASE_TYPE_LIST.map((type) => ({
          value: type,
          label: t(`purchase_type.${type}`),
        })),
      },
      cell: ({ row }) => String(t(`purchase_type.${row.original.type}`)),
    },
    {
      id: "price",
      accessorFn: (row) => row.price ?? 0,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("price")} />
      ),
      meta: {
        label: t("price"),
        width: "10%",
        minWidth: 110,
      },
      cell: ({ row }) => formatPrice(row.original.price, row.original.currency),
    },
    figureColumn("total_sales", t("total_sales"), {
      width: "13%",
      minWidth: 160,
    }),
    figureColumn("direct_sales", t("direct_sales"), {
      width: "12%",
      minWidth: 140,
    }),
    figureColumn("gifts", t("gifts"), { width: "9%", minWidth: 110 }),
  ];
}
